import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import toast from "react-hot-toast";
import { PARENT_URL } from "../utils/branding";
import { setToken } from "../utils/tokenStorage";
import Logo from "../components/Logo";
import { Mail, KeyRound, ArrowRight, ArrowLeft } from "lucide-react";

export default function Login() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  // A ?target= carried from the landing hero means the visitor already told
  // us what they want scanned — send them to the scanner with it prefilled
  // instead of dropping them on an empty dashboard to retype it.
  const [params] = useSearchParams();
  const carried = (params.get("target") || "").trim();
  const afterAuth = () => (carried ? `/scan?target=${encodeURIComponent(carried)}` : "/dashboard");

  const sendCode = async () => {
    if (!email.trim()) { toast.error("Enter email"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/login", { email: email.trim() });
      toast.success("Code sent to your email");
      setStep(2);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) { toast.error("Enter code"); return; }
    setBusy(true);
    try {
      const res = await api.post("/api/auth/verify", {
        email: email.trim(),
        code: code.trim(),
        purpose: "login"
      });
      setToken("aegis_token", res.data.access_token);
      toast.success("Welcome back!");
      setTimeout(() => nav(afterAuth()), 500);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: "var(--au-text-2)",
    letterSpacing: 2,
    marginBottom: 10,
    fontWeight: 600,
    textTransform: "uppercase"
  };

  return (
    <div className="au-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 20, position: "relative" }}>
      {/* BACK BUTTON */}
      <a className="au-pill au-pill-glass au-pill-sm" style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }} href={PARENT_URL}>
        <ArrowLeft size={16} /> Back
      </a>

      {/* subtle accent radial */}
      <div style={{
        position: "absolute",
        top: "-200px",
        right: "-200px",
        width: 500,
        height: 500,
        background: "radial-gradient(circle, var(--au-accent-glow), transparent 70%)",
        borderRadius: "50%",
        filter: "blur(60px)",
        opacity: 0.4,
        pointerEvents: "none"
      }} />

      {/* MAIN CARD */}
      <div className="au-glass au-pad-lg" style={{ maxWidth: 440, width: "100%", position: "relative", zIndex: 5 }}>
        {/* BRAND HEADER */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ marginBottom: 10 }}>
            <Logo size="large" />
          </div>
          <div style={{
            fontSize: 10,
            letterSpacing: 4,
            color: "var(--au-muted)",
            textTransform: "uppercase"
          }}>
            DETECT · DEFEND · SECURE
          </div>
        </div>

        {/* FORM */}
        {step === 1 ? (
          <>
            <label style={labelStyle}>
              <Mail size={14} /> Email Address
            </label>
            <input
              className="au-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !busy && sendCode()}
              placeholder="you@company.com"
              autoFocus
              style={{ marginBottom: 20 }}
            />
            <button
              className="au-pill au-pill-primary"
              onClick={sendCode}
              disabled={busy}
              style={{ width: "100%" }}>
              {busy ? "SENDING..." : "Send Login Code"} <ArrowRight size={18} />
            </button>
          </>
        ) : (
          <>
            <div style={{
              padding: "11px 14px",
              background: "var(--au-glass-strong)",
              border: "1px solid var(--au-border)",
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 12,
              color: "var(--au-text-2)",
              textAlign: "center"
            }}>
              Code sent to <strong style={{ color: "var(--au-accent-ink)" }}>{email}</strong>
            </div>
            <label style={labelStyle}>
              <KeyRound size={14} /> Verification Code
            </label>
            <input
              className="au-input"
              type="text"
              value={code}
              onChange={e => {
                let v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);
                if (v.length > 6) v = v.slice(0,3) + "-" + v.slice(3,6) + "-" + v.slice(6);
                else if (v.length > 3) v = v.slice(0,3) + "-" + v.slice(3);
                setCode(v);
              }}
              onKeyDown={e => e.key === "Enter" && !busy && verify()}
              placeholder="XXX-XXX-XXX"
              autoFocus
              maxLength={11}
              style={{
                fontFamily: "var(--au-font-mono)",
                fontSize: 24,
                textAlign: "center",
                letterSpacing: 12,
                fontWeight: 700,
                marginBottom: 20
              }}
            />
            <button
              className="au-pill au-pill-primary"
              onClick={verify}
              disabled={busy}
              style={{ width: "100%" }}>
              {busy ? "VERIFYING..." : "Verify & Login"} <ArrowRight size={18} />
            </button>
            <button
              className="au-pill au-pill-ghost"
              onClick={() => { setStep(1); setCode(""); }}
              style={{ width: "100%", marginTop: 12 }}>
              <ArrowLeft size={16} /> Use different email
            </button>
          </>
        )}

        {/* FOOTER */}
        <div style={{
          textAlign: "center",
          marginTop: 28,
          paddingTop: 22,
          borderTop: "1px solid var(--au-hair)",
          fontSize: 13,
          color: "var(--au-text-2)"
        }}>
          No account?{" "}
          <Link to="/register" style={{
            color: "var(--au-accent-ink)",
            textDecoration: "none",
            fontWeight: 600
          }}>
            Register free
          </Link>
        </div>
      </div>
    </div>
  );
}
